import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { LoginComponent } from './components/core/login/login.component';
import { Error404Component } from './components/core/error404/error404.component';
import { AuthGuard } from './guards/auth.guard';
import { HandspraySeasonsResolverService } from './services/resolvers/handspray/seasons-resolver.service';
import { HandspraySiteResolverService } from './services/resolvers/handspray/site-resolver.service';
import { HandsprayVisitResolverService } from './services/resolvers/handspray/visit-resolver.service';
import { HandsprayMapResolverService } from './services/resolvers/handspray/map-resolver.service';
import { HandsprayVisitListComponent } from './components/handspray/handspray-site/handspray-visit-list/handspray-visit-list.component';
import { HandsprayVisitListResolverService } from './services/resolvers/handspray/visit-list-resolver.service';
import { AccountComponent } from './components/core/account/account.component';
import { WeedfreeFormMmtComponent } from './components/weedfree/forms/weedfree-form-mmt/weedfree-form-mmt.component';
import { HandsprayMapComponent } from './components/handspray/handspray-map/handspray-map.component';
import { HandspraySiteComponent } from './components/handspray/handspray-site/handspray-site.component';
import { HandsprayVisitComponent } from './components/handspray/handspray-site/handspray-visit/handspray-visit.component';
import { MmtVisitsComponent } from './components/mmt/mmt-visits/mmt-visits.component';
import { MmtVisitListResolverService } from './services/resolvers/mmt/visit-list-resolver.service';
import { MmtVisitComponent } from './components/mmt/mmt-visit/mmt-visit.component';
import { MmtVisitResolverService } from './services/resolvers/mmt/visit-resolver.service';
import { MmtSiteMachineListResolverService } from './services/resolvers/mmt/site-machine-list-resolver.service';
import { MmtSiteCoordResolverService } from './services/resolvers/mmt/mmt-site-coord-resolver.service';
import { UploadInProgressGuard } from './guards/upload-in-progress.guard';
import { MmtMapComponent } from './components/mmt/mmt-map/mmt-map.component';
import { ClientDocumentsComponent } from './components/client-documents/client-documents.component';
import { ClientDocumentsResolverService } from './services/resolvers/client-documents-resolver.service';
import { WeedfreeFormMmtCovidComponent } from './components/weedfree/forms/weedfree-form-mmt-covid/weedfree-form-mmt-covid.component';
import { MmtCovidVisitComponent } from './components/mmt/mmt-covid-visit/mmt-covid-visit.component';
import { CovidSiteMachineListResolverService } from './services/resolvers/mmt/covid-site-machine-list-resolver.service';
import { CovidVisitListResolverService } from './services/resolvers/mmt/covid-visit-list-resolver.service';
import { MmtCovidVisitResolverService } from './services/resolvers/mmt/mmt-covid-visit-resolver.service';
import { WeedfreeClientDocumentsComponent } from './components/weedfree/weedfree-client-documents/weedfree-client-documents.component';
import { ClientListResolverService } from './services/resolvers/client-list-resolver.service';
import { ClientDocumentListComponent } from './components/weedfree/weedfree-client-documents/client-document-list/client-document-list.component';
import { PasswordPolicyComponent } from './components/core/account/password-policy/password-policy.component';
import { WeedfreeFormLegionellaComponent } from './components/weedfree/forms/weedfree-form-legionella/weedfree-form-legionella.component';
import { LegionellaVisitsComponent } from './components/legionella/legionella-visits/legionella-visits.component';
import { LegionellaSiteMachineListResolverService } from './services/resolvers/legionella/legionella-site-machine-list-resolver.service';
import { LegionellaVisitListResolverService } from './services/resolvers/legionella/legionella-visit-list-resolver.service';
import { LegionellaVisitResolverService } from './services/resolvers/legionella/legionella-visit-resolver.service';
import { LegionellaVisitComponent } from './components/legionella/legionella-visit/legionella-visit.component';

const routes: Routes = [
  { path: '', pathMatch: 'full', redirectTo: '/support-service/visits' },
  {
    path: 'certificates',
    canActivate: [AuthGuard],
    component: ClientDocumentsComponent,
    resolve: { documents: ClientDocumentsResolverService }
  },
  {
    path: 'handspray',
    canActivate: [AuthGuard],
    children: [
      { path: '', pathMatch: 'full', redirectTo: '/handspray/map' },
      {
        path: 'map',
        component: HandsprayMapComponent,
        resolve: {
          seasons: HandspraySeasonsResolverService,
          sites: HandsprayMapResolverService
        }
      },
      {
        path: 'map/:season',
        component: HandsprayMapComponent,
        resolve: {
          seasons: HandspraySeasonsResolverService,
          sites: HandsprayMapResolverService
        }
      },
      {
        path: 'site/:site',
        component: HandspraySiteComponent,
        resolve: { site: HandspraySiteResolverService },
        children: [
          {
            path: '',
            pathMatch: 'full',
            component: HandsprayVisitListComponent,
            resolve: { visits: HandsprayVisitListResolverService }
          },
          {
            path: 'visit/:visit',
            component: HandsprayVisitComponent,
            resolve: { visit: HandsprayVisitResolverService }
          }
        ]
      }
    ]
  },
  {
    path: 'support-service',
    canActivate: [AuthGuard],
    children: [
      {
        path: 'map',
        component: MmtMapComponent,
        resolve: {
          sites: MmtSiteCoordResolverService,
        }
      },
      {
        path: 'covid-visits/:type/:filter',
        component: MmtVisitsComponent,
        resolve: {
          sites: CovidSiteMachineListResolverService,
          visits: CovidVisitListResolverService
        }
      },
      {
        path: 'visits/:type/:filter',
        component: MmtVisitsComponent,
        resolve: {
          sites: MmtSiteMachineListResolverService,
          visits: MmtVisitListResolverService,
        }
      },
      {
        path: 'visits',
        component: MmtVisitsComponent,
        resolve: {
          sites: MmtSiteMachineListResolverService,
          visits: MmtVisitListResolverService
        }
      },
      {
        path: 'visit/:id',
        component: MmtVisitComponent,
        resolve: { visit: MmtVisitResolverService }
      },
      {
        path: 'covid-visits',
        component: MmtVisitsComponent,
        resolve: {
          sites: CovidSiteMachineListResolverService,
          visits: CovidVisitListResolverService
        }
      },
      {
        path: 'covid-visit/:id',
        component: MmtCovidVisitComponent,
        resolve: {
          visit: MmtVisitResolverService,
          covid: MmtCovidVisitResolverService,
        }
      },
      {
        path: 'legionella-tests',
        component: LegionellaVisitsComponent,
        resolve: {
          sites: LegionellaSiteMachineListResolverService,
          visits: LegionellaVisitListResolverService,
        }
      },
      {
        path: 'legionella-tests/:type/:filter',
        component: LegionellaVisitsComponent,
        resolve: {
          sites: LegionellaSiteMachineListResolverService,
          visits: LegionellaVisitListResolverService,
        }
      },
      {
        path: 'legionella-test/:id',
        component: LegionellaVisitComponent,
        resolve: {
          visit: LegionellaVisitResolverService,
        }
      }
    ]
  },
  {
    path: 'weedfree/forms/support-service',
    component: WeedfreeFormMmtComponent,
    canActivate: [AuthGuard],
    canDeactivate: [UploadInProgressGuard]
  },
  {
    path: 'weedfree/forms/support-service/covid/:id',
    component: WeedfreeFormMmtCovidComponent,
    canActivate: [AuthGuard],
    canDeactivate: [UploadInProgressGuard]
  },
  {
    path: 'weedfree/forms/legionella',
    component: WeedfreeFormLegionellaComponent,
    canActivate: [AuthGuard],
    canDeactivate: [UploadInProgressGuard]
  },
  {
    path: 'weedfree/client-documents',
    component: WeedfreeClientDocumentsComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: ':client',
        component: ClientDocumentListComponent,
        resolve: {
          documents: ClientDocumentsResolverService
        }
      }
    ],
    resolve: {
      clients: ClientListResolverService
    }
  },
  {
    path: 'login',
    component: LoginComponent
  },
  {
    path: 'account',
    component: AccountComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'account/password-policy',
    component: PasswordPolicyComponent
  },
  {
    path: '**',
    component: Error404Component
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { relativeLinkResolution: 'legacy' })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
